import { useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UFS, digits, maskCep, lookupCep } from "@/lib/brdocs";

export const EMPTY_ADDRESS = { cep: "", street: "", number: "", complement: "", district: "", city: "", state: "" };

const F = ({ label, className = "", ...p }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <Input className="h-11 rounded-xl bg-white/5" {...p} />
  </div>
);

export function AddressFields({ value, onChange, prefix = "address" }) {
  const [state, setState] = useState({ loading: false, error: "" });
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });

  const onCep = async (e) => {
    const cep = maskCep(e.target.value);
    onChange({ ...value, cep });
    if (digits(cep).length !== 8) return setState({ loading: false, error: "" });
    setState({ loading: true, error: "" });
    try {
      const a = await lookupCep(cep);
      onChange({ ...value, cep, ...Object.fromEntries(Object.entries(a).filter(([, v]) => v)) });
      setState({ loading: false, error: "" });
    } catch (err) {
      setState({ loading: false, error: err.message === "CEP não encontrado" ? err.message : "Não foi possível consultar o CEP. Preencha manualmente." });
    }
  };

  return (
    <div className="grid grid-cols-6 gap-3" data-testid={`${prefix}-fields`}>
      <div className="col-span-6 space-y-1.5 sm:col-span-2">
        <Label className="text-xs text-muted-foreground">CEP</Label>
        <div className="relative">
          <Input required inputMode="numeric" className="h-11 rounded-xl bg-white/5 pr-9" value={value.cep} onChange={onCep} placeholder="00000-000" data-testid={`${prefix}-cep-input`} />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{state.loading ? <Loader2 size={15} className="animate-spin" /> : <MapPin size={15} />}</span>
        </div>
        {state.error && <p className="text-[11px] text-amber-300" data-testid={`${prefix}-cep-error`}>{state.error}</p>}
      </div>
      <F required label="Rua / Avenida" className="col-span-6 sm:col-span-4" value={value.street} onChange={set("street")} data-testid={`${prefix}-street-input`} />
      <F required label="Número" className="col-span-2" value={value.number} onChange={set("number")} data-testid={`${prefix}-number-input`} />
      <F label="Complemento" className="col-span-4" value={value.complement} onChange={set("complement")} placeholder="Opcional" data-testid={`${prefix}-complement-input`} />
      <F required label="Bairro" className="col-span-6 sm:col-span-2" value={value.district} onChange={set("district")} data-testid={`${prefix}-district-input`} />
      <F required label="Cidade" className="col-span-4 sm:col-span-3" value={value.city} onChange={set("city")} data-testid={`${prefix}-city-input`} />
      <div className="col-span-2 space-y-1.5 sm:col-span-1">
        <Label className="text-xs text-muted-foreground">UF</Label>
        <Select value={value.state} onValueChange={(v) => onChange({ ...value, state: v })}>
          <SelectTrigger className="h-11 rounded-xl bg-white/5" data-testid={`${prefix}-state-select`}><SelectValue placeholder="UF" /></SelectTrigger>
          <SelectContent>{UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  );
}

export const addressComplete = (a) => digits(a.cep).length === 8 && a.street && a.number && a.district && a.city && a.state;
